import { Tooltip, Typography, Theme, useTheme } from "@material-ui/core";
import React from "react";
import {
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  Record,
  TextField,
  TextInput,
  useListContext,
  Responsive,
  SimpleList,
  useTranslate,
} from "react-admin";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import resourceNames from "../../../common/data/resourceNames";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { Void } from "../../common/data-grid-fields/Void";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { TiresStatusField } from "./TiresStatusField";
import { VehicleMaintenanceSummaryDto } from "./types";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import BusinessIcon from "@material-ui/icons/Business";
import EventIcon from "@material-ui/icons/Event";
import SpeedIcon from "@material-ui/icons/Speed";
import LocalCarWashIcon from "@material-ui/icons/LocalCarWash";
import DateRangeIcon from "@material-ui/icons/DateRange";
import ShowIcon from "@material-ui/icons/RemoveRedEye";
import { Link } from "react-router-dom";

interface Props {}

const ListFilter = (props: any) => {
  useSearchFields(props, ["vehicleName"]);
  useResetListSearchField(props);
  const { loading } = useListContext();

  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="malfunctions-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="malfunctions-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
    </Filter>
  );
};

interface SimpleListProps {
  record?: Record;
  isAllowed?: Boolean;
}

const RenderPrimaryTextForSimpleList = ({
  record,
  isAllowed,
}: SimpleListProps) => {

  const translate = useTranslate();
  const theme: Theme = useTheme();
  
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        width: `${isAllowed ? '85%' : '100%'}`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tooltip
          title={translate("resources.assets-maintenance.labels.vehicleName")}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <LocalShippingOutlinedIcon
              style={{
                color: theme.palette.primary.main,
              }}
            />
            <TextField
              source="vehicleName"
              label="resources.assets-maintenance.labels.vehicleName"
            />
          </div>
        </Tooltip>
        <Tooltip
          title={translate("resources.assets-maintenance.labels.vehicleMake")}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <BusinessIcon
              style={{
                color: theme.palette.primary.main,
              }}
            />
            <TextField
              source="vehicleMake"
              label="resources.assets-maintenance.labels.vehicleMake"
            />
          </div>
        </Tooltip>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tooltip
          title={translate("resources.assets-maintenance.labels.vehicleYear")}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <EventIcon
              style={{
                color: theme.palette.primary.main,
              }}
            />
            <TextField
              source="vehicleYear"
              label="resources.assets-maintenance.labels.vehicleYear"
            />
          </div>
        </Tooltip>
        <Tooltip
          title={translate(
            "resources.assets-maintenance.labels.vehicleOdometer"
          )}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <SpeedIcon
              style={{
                color: theme.palette.primary.main,
              }}
            />
            <FunctionField<VehicleMaintenanceSummaryDto & Record>
              source="vehicleOdometer"
              label="resources.assets-maintenance.labels.vehicleOdometer"
              render={(record) =>
                record && (
                  <Typography variant="body2">
                    {record.vehicleOdometer} {record.vehicleOdometerUnit}
                  </Typography>
                )
              }
            />
          </div>
        </Tooltip>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tooltip
          title={translate("resources.assets-maintenance.labels.tiresStatus")}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <LocalCarWashIcon
              style={{
                color: theme.palette.primary.main,
              }}
            />
            <TiresStatusField
              record={record as VehicleMaintenanceSummaryDto & Record}
              label="resources.assets-maintenance.labels.tiresStatus"
            />
          </div>
        </Tooltip>
        <Tooltip
          title={translate(
            "resources.assets-maintenance.labels.lastMaintenanceDate"
          )}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <DateRangeIcon
              style={{
                color: theme.palette.primary.main,
              }}
            />
            <DateField
              source="lastMaintenanceDate"
              label="resources.assets-maintenance.labels.lastMaintenanceDate"
            />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export const VehicleMaintenanceSummaryList = (props: Props) => {
  const permissions = useActionPermissions();
  const theme: Theme = useTheme();

  return (
    <div className="content-container">
      <DefaultProLogsList
        title="resources.assets-maintenance.name"
        actions={<Void />}
        bulkActionButtons={false}
        filters={<ListFilter />}
        {...props}
      >
        <Responsive
          small={
            <>
              <SimpleList
                className="simple-list-wrapper"
                linkType="show"
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList
                    record={record}
                    isAllowed={
                      permissions[permissionNames.assets.maintenance.view]
                    }
                  />
                )}
                rightIcon={(record) =>
                  permissions[permissionNames.assets.maintenance.view] ? (
                    <Link to={`/assets-vehicle-maintenance/${record.id}`}>
                      <ShowIcon
                        style={{
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Link>
                  ) : null
                }
              />
            </>
          }
          medium={
            <Datagrid
              rowClick={
                permissions[permissionNames.assets.maintenance.view]
                  ? (id, _basePath, _record) => {
                      return `/${resourceNames.ASSETS_VEHICLE_MAINTENANCE}/${id}`;
                    }
                  : undefined
              }
            >
              <TextField
                source="vehicleName"
                label="resources.assets-maintenance.labels.vehicleName"
              />
              <TextField
                source="vehicleMake"
                label="resources.assets-maintenance.labels.vehicleMake"
              />
              <TextField
                source="vehicleYear"
                label="resources.assets-maintenance.labels.vehicleYear"
              />
              <FunctionField<VehicleMaintenanceSummaryDto & Record>
                source="vehicleOdometer"
                label="resources.assets-maintenance.labels.vehicleOdometer"
                render={(record) =>
                  record && (
                    <Typography variant="body2">
                      {record.vehicleOdometer} {record.vehicleOdometerUnit}
                    </Typography>
                  )
                }
              />
              <TiresStatusField label="resources.assets-maintenance.labels.tiresStatus" />
              <DateField
                source="lastMaintenanceDate"
                label="resources.assets-maintenance.labels.lastMaintenanceDate"
              />
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
